import { ApiService } from '@zola-helpers/client/dist/es';
import {
  getLocalStorage,
  setLocalStorage,
  deleteLocalStorage,
} from '@zola-helpers/client/dist/es/util/storage';
import type { AccountIncentiveStatusView } from '@zola/svc-marketplace-ts-types';
import { useEffect } from 'react';

/**
 * Create a local storage key for an account incentive.
 *
 * @see markPreAuthUserAsHavingSeenIncentive
 *
 * @see useAccountIncentivizationCheck
 */
const preAuthUserSessionStorageIncentiveKey = (
  offerType: AccountIncentiveStatusView.OfferTypeEnum
): string => {
  return `zola-account-incentive-seen-${offerType}`;
};

/**
 * For a pre-auth user, record in local storage that they have seen the incentive.
 *
 * Later, if the same browser is used by an authenticated account, we'll look for
 * this local storage key and activate the incentive if it's found.
 *
 * @see useAccountIncentivizationCheck
 *
 * @param offerType - The offer to record as seen
 */
export const markPreAuthUserAsHavingSeenIncentive = (
  offerType: AccountIncentiveStatusView.OfferTypeEnum
): void => {
  setLocalStorage(preAuthUserSessionStorageIncentiveKey(offerType), 'true');
};

// This record is here to make sure that if a new incentive is added, we don't forget to add it to the list
const OFFER_TYPE_HASH: Record<AccountIncentiveStatusView.OfferTypeEnum, boolean> = {
  JUNE2024_BOOKEDINQUIRY_65OFFSTD: true,
};
const ALL_OFFER_TYPES = Object.keys(OFFER_TYPE_HASH) as AccountIncentiveStatusView.OfferTypeEnum[];

/**
 * For a post-auth user, look to see if they previously have seen an incentive
 * as a pre-auth user.  If they have, activate the incentive and clear the local
 * storage key
 *
 * @param userContext
 */
export const useAccountIncentivizationCheck = (
  userContext: { is_guest?: boolean } | null
): void => {
  useEffect(() => {
    if (userContext && !userContext.is_guest) {
      ALL_OFFER_TYPES.forEach((offerType) => {
        const localStorageKey = preAuthUserSessionStorageIncentiveKey(offerType);

        if (getLocalStorage(localStorageKey) === 'true') {
          // Where should this API call go?
          ApiService.post('/web-marketplace-api/v1/account-incentive/activate-offer', {
            offer_type: offerType,
          })
            .then(() => {
              deleteLocalStorage(localStorageKey);
            })
            .catch(() => null);
        }
      });
    }
  }, [userContext?.is_guest]);
};
